import { Skeleton, Alert } from 'antd'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchPublicHome, type PublicApiError, type PublicHome } from './publicApi'

export default function PublicHomePage({ workspaceSlug, siteSlug }: { workspaceSlug: string; siteSlug: string }) {
  const [data, setData] = useState<PublicHome | null>(null)
  const [error, setError] = useState<string | null>(null)
  const base = `/site/${workspaceSlug}/${siteSlug}`

  useEffect(() => {
    setData(null)
    setError(null)
    fetchPublicHome(workspaceSlug, siteSlug)
      .then((r) => setData(r.data))
      .catch((err) => setError((err as PublicApiError).message))
  }, [workspaceSlug, siteSlug])

  if (error) return <div className="pub-page"><Alert type="error" message={error} showIcon /></div>
  if (!data) return <div className="pub-page"><Skeleton active paragraph={{ rows: 8 }} /></div>

  return (
    <div className="pub-page">
      <section className="pub-hero">
        {data.hero_image_url && <img className="pub-hero-image" src={data.hero_image_url} alt={data.site.name} />}
        <h1>{data.site.name}</h1>
        <p>{data.hero_text}</p>
      </section>

      {data.featured_items.length > 0 && (
        <>
          <h2 className="pub-section-title">
            精选条目
            <Link to={`${base}/items`}>查看全部条目 ({data.item_count}) →</Link>
          </h2>
          <div className="pub-grid">
            {data.featured_items.map((item) => (
              <Link key={item.slug} to={`${base}/items/${item.slug}`} className="pub-card">
                <div className="pub-card-cover">
                  {item.cover_url && <img src={item.cover_url} alt={item.label} />}
                  {item.category && <span className="pub-card-badge">{item.category.name}</span>}
                </div>
                <div className="pub-card-body">
                  <p className="pub-card-title">{item.label}</p>
                  <p className="pub-card-meta">{item.creator || item.era || item.accession_number}</p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {data.featured_exhibitions.length > 0 && (
        <>
          <h2 className="pub-section-title">
            展览
            <Link to={`${base}/exhibitions`}>查看全部展览 →</Link>
          </h2>
          <div className="pub-grid">
            {data.featured_exhibitions.map((e) => (
              <Link key={e.slug} to={`${base}/exhibitions/${e.slug}`} className="pub-card">
                <div className="pub-card-cover">{e.cover_url && <img src={e.cover_url} alt={e.label} />}</div>
                <div className="pub-card-body">
                  <p className="pub-card-title">{e.label}</p>
                  <p className="pub-card-meta">{e.venue || `${e.start_date} – ${e.end_date}`}</p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}

      {data.featured_research.length > 0 && (
        <>
          <h2 className="pub-section-title">
            研究
            <Link to={`${base}/research`}>查看全部研究 →</Link>
          </h2>
          <div className="pub-grid">
            {data.featured_research.map((r) => (
              <Link key={r.slug} to={`${base}/research/${r.slug}`} className="pub-card">
                <div className="pub-card-cover">{r.cover_url && <img src={r.cover_url} alt={r.label} />}</div>
                <div className="pub-card-body">
                  <p className="pub-card-title">{r.label}</p>
                  <p className="pub-card-meta">{r.author}</p>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
